import { lazy, Suspense } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { useTheme } from "./hooks/useTheme";
import HomePage from "./pages/HomePage";
import ProtectedRoute from "./components/ProtectedRoute";
import "@/frontend/index.css";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const InvoicePrintPage = lazy(() => import("./pages/InvoicePrintPage"));
const PaymentRedirectPage = lazy(() => import("./pages/PaymentRedirectPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const CookieConsent = lazy(() => import("./components/CookieConsent"));

export default function App() {
  const { pathname } = useLocation();
  // The landing is the catch-all route and is light by design, so its route
  // pins light tokens for the app-level chrome too — otherwise the cookie
  // notice renders with the dark theme over a light page. Every other route
  // follows the visitor's theme. Keep this list in step with the routes below.
  const isAppRoute =
    /^\/(?:login|forgot-password|reset-password|cookies-policy|privacy-policy|terms|dashboard|invoice|payment)(?:\/|$)/.test(
      pathname,
    );
  const { theme, toggle } = useTheme({ forceLight: !isAppRoute });

  return (
    <div className="min-h-screen bg-surface text-text font-sans antialiased">
      <a href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-9999 focus:px-4 focus:py-3 focus:bg-primary focus:text-primary-foreground focus:rounded-lg focus:font-semibold focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-white/50">
        Skip to content
      </a>
      <Suspense fallback={null}>
        <CookieConsent />
        <Routes>
          <Route path="/login" element={<LoginPage theme={theme} toggle={toggle} />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage theme={theme} toggle={toggle} />} />
          <Route path="/reset-password" element={<ResetPasswordPage theme={theme} toggle={toggle} />} />
          <Route path="/cookies-policy" element={<CookiePolicyPage theme={theme} toggle={toggle} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage theme={theme} toggle={toggle} />} />
          <Route path="/terms" element={<TermsPage theme={theme} toggle={toggle} />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage theme={theme} toggle={toggle} /></ProtectedRoute>} />
          <Route path="/invoice/:id/print" element={<InvoicePrintPage />} />
          <Route path="/payment/success" element={<PaymentRedirectPage />} />
          <Route path="/payment/cancelled" element={<PaymentRedirectPage />} />
          <Route path="/" element={<HomePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </div>
  );
}
